import { memo } from 'react'
import { Box } from '@mui/material'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { SxProps, Theme } from '@mui/system'

interface PaginationProps {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  maxVisiblePages?: number
  styles: {
    paginationContainer: SxProps<Theme>
    paginationButton: SxProps<Theme>
    paginationPage: SxProps<Theme>
    paginationPageActive: SxProps<Theme>
    paginationDisabled: SxProps<Theme>
  }
}

const getVisiblePages = (
  current: number,
  total: number,
  maxVisible: number
): (number | string)[] => {
  // Para pocos pages totales, mostrar todos
  if (total <= maxVisible + 2) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  // Para maxVisible pequeño (<= 3), usar lógica simple
  if (maxVisible <= 3) {
    if (current <= 2) {
      const pages: (number | string)[] = []
      for (let i = 1; i <= Math.min(maxVisible, total); i++) {
        pages.push(i)
      }
      if (!pages.includes(total)) {
        pages.push('...')
        pages.push(total)
      }
      return pages
    }
    if (current >= total - 1) {
      const pages: (number | string)[] = [1, '...']
      for (let i = Math.max(1, total - maxVisible + 1); i <= total; i++) {
        pages.push(i)
      }
      return pages
    }
    return [1, '...', current, '...', total]
  }

  // Para maxVisible grande (> 3), usar ventana deslizante
  const pages: (number | string)[] = [1]

  const halfVisible = Math.floor(maxVisible / 2)
  let start = Math.max(2, current - halfVisible)
  let end = Math.min(total - 1, current + halfVisible)

  // Ajustar si estamos cerca del inicio
  if (current <= halfVisible + 1) {
    end = Math.min(total - 1, maxVisible + 1)
  }

  // Ajustar si estamos cerca del final
  if (current >= total - halfVisible) {
    start = Math.max(2, total - maxVisible)
  }

  if (start > 2) {
    pages.push('...')
  } else if (start === 2) {
    pages.push(2)
  }

  for (let i = Math.max(2, start); i <= end; i++) {
    if (!pages.includes(i)) {
      pages.push(i)
    }
  }

  if (end < total - 1) {
    pages.push('...')
  } else if (end === total - 1) {
    pages.push(total - 1)
  }

  if (!pages.includes(total)) {
    pages.push(total)
  }

  return pages
}

const Pagination = ({
  page,
  totalPages,
  onPageChange,
  maxVisiblePages = 3,
  styles,
}: PaginationProps) => {
  if (totalPages <= 1) return null

  const visiblePages = getVisiblePages(page, totalPages, maxVisiblePages)

  return (
    <Box sx={styles.paginationContainer}>
      {/* << Primera */}
      <Box
        sx={page === 1 ? styles.paginationDisabled : styles.paginationButton}
        onClick={() => page > 1 && onPageChange(1)}
      >
        <FirstPageIcon sx={{ fontSize: '24px' }} />
      </Box>
      {/* < Anterior */}
      <Box
        sx={page === 1 ? styles.paginationDisabled : styles.paginationButton}
        onClick={() => page > 1 && onPageChange(page - 1)}
      >
        <ChevronLeftIcon sx={{ fontSize: '24px' }} />
      </Box>
      {/* Números */}
      {visiblePages.map((p, i) =>
        typeof p === 'number' ? (
          <Box
            key={i}
            sx={p === page ? styles.paginationPageActive : styles.paginationPage}
            onClick={() => onPageChange(p)}
          >
            {p}
          </Box>
        ) : (
          <Box key={i} sx={styles.paginationPage}>
            {p}
          </Box>
        )
      )}
      {/* > Siguiente */}
      <Box
        sx={page === totalPages ? styles.paginationDisabled : styles.paginationButton}
        onClick={() => page < totalPages && onPageChange(page + 1)}
      >
        <ChevronRightIcon sx={{ fontSize: '24px' }} />
      </Box>
      {/* >> Última */}
      <Box
        sx={page === totalPages ? styles.paginationDisabled : styles.paginationButton}
        onClick={() => page < totalPages && onPageChange(totalPages)}
      >
        <LastPageIcon sx={{ fontSize: '24px' }} />
      </Box>
    </Box>
  )
}

export { Pagination, getVisiblePages }
